import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, User, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { googleSheetsService } from '../services/googleSheetsService';
import './Popup.css';

interface StudentPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  type: string;
  name: string;
  phone: string;
  location: string;
  message: string;
}

const StudentPopup: React.FC<StudentPopupProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    type: '',
    name: '',
    phone: '',
    location: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      type: e.target.value
    }));
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];
    
    if (!formData.type.trim()) errors.push('Please select Partner or Job Seeker');
    if (!formData.name.trim()) errors.push('Name is required');
    if (!formData.phone.trim()) errors.push('Phone number is required');
    if (!formData.location.trim()) errors.push('Location is required');
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit to main Google Sheet
      const mainSheetSuccess = await googleSheetsService.submitToSheet(formData);
      
      // Also submit to backup sheet
      const backupSheetSuccess = await googleSheetsService.submitToBackupSheet(formData);
      
      if (mainSheetSuccess && backupSheetSuccess) {
        setIsSubmitted(true);
        setIsSubmitting(false);
        
        // Reset form after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            type: '',
            name: '',
            phone: '',
            location: '',
            message: ''
          });
          onClose();
        }, 5000);
      } else {
        throw new Error('Failed to submit to Google Sheets');
      }
    } catch (error) {
      setErrors(['Failed to submit form. Please try again.']);
      setIsSubmitting(false);
    }
  };
  return (
    <>
      {isOpen && (
        <div
          className="popup-overlay"
          onClick={onClose}
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            zIndex: 10000,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15), 0 10px 20px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              color: '#1f2937'
            }}
          >
            <div className="popup-header">
              <div className="popup-title">
                <User size={24} />
                <h3>Get Started</h3>
              </div>
              <button className="popup-close" onClick={onClose}>
                <X size={24} />
              </button>
            </div>

            <div className="popup-body">
              <p className="popup-description">
                Fill out the form below and we'll help you connect with the right opportunities.
              </p>

              {errors.length > 0 && (
                <div className="error-message">
                  <h4>Please fix the following errors:</h4>
                  <ul>
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {isSubmitted ? (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <CheckCircle size={48} />
                  <h4>Registration Successful!</h4>
                  <p>Thank you for registering. Our team will contact you within 24 hours to discuss opportunities.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="popup-form">
                  <div className="form-group">
                    <label style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>I am a:</label>
                        <div className="radio-group">
                          <label className="radio-label" style={{ fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>
                            <input
                              type="radio"
                              name="type"
                              value="Job Seeker"
                              checked={formData.type === 'Job Seeker'}
                              onChange={handleRadioChange}
                            />
                            <span>Job Seeker</span>
                          </label>
                          <label className="radio-label" style={{ fontSize: '0.95rem', fontWeight: '500', color: '#374151' }}>
                            <input
                              type="radio"
                              name="type"
                              value="Partner"
                              checked={formData.type === 'Partner'}
                              onChange={handleRadioChange}
                            />
                            <span>Partner</span>
                          </label>
                        </div>
                  </div>

                      <div className="form-group">
                        <label htmlFor="name" style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                          <User size={16} />
                          Name *
                        </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>

                      <div className="form-group">
                        <label htmlFor="phone" style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                          <Phone size={16} />
                          Phone Number *
                        </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your phone number"
                    />
                  </div>

                      <div className="form-group">
                        <label htmlFor="location" style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                          <MapPin size={16} />
                          Location *
                        </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      placeholder="City, State"
                    />
                  </div>

                      <div className="form-group">
                        <label htmlFor="message" style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Leave us a message (optional)"
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary form-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Submit
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentPopup;
